import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import "./datatable.scss";

const STATUSES = ["ACTIVE", "COMPLETED", "WITHDRAWN", "CHANGED"];

const STATUS_COLORS = {
  ACTIVE: "#1976d2",
  COMPLETED: "#2e7d32",
  WITHDRAWN: "#c62828",
  CHANGED: "#f57c00",
};

const emptyEnrollForm = {
  id: null,
  studentId: "",
  degreeId: "",
  programId: "",
  startDate: "",
  endDate: "",
  status: "ACTIVE",
};

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 520, background: "#fff", borderRadius: 8,
        padding: 24, boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        {children}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{label}</label>
    {children}
  </div>
);

const iStyle = {
  width: "100%", padding: "8px", borderRadius: 4,
  border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box",
};

const btn = (bg) => ({
  padding: "7px 16px", background: bg, color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13,
});

export const StudentEnrollmentList = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Enrollment modal
  const [enrollModal, setEnrollModal] = useState(false);
  const [enrollForm, setEnrollForm] = useState(emptyEnrollForm);

  // Degree modal
  const [degreeModal, setDegreeModal] = useState(false);
  const [degreeName, setDegreeName] = useState("");

  // Program modal
  const [programModal, setProgramModal] = useState(false);
  const [programForm, setProgramForm] = useState({ id: null, programName: "", degreeId: "" });

  const filteredPrograms = programs.filter(
    (p) => !enrollForm.degreeId || String(p.degreeId) === String(enrollForm.degreeId)
  );

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [eRes, dRes, pRes, sRes] = await Promise.all([
        axiosInstance.get("/getAllEnrollments"),
        axiosInstance.get("/getAllDegrees"),
        axiosInstance.get("/getAllPrograms"),
        axiosInstance.get("/students"),
      ]);
      setEnrollments(eRes.data?.data || []);
      setDegrees(dRes.data?.data || []);
      setPrograms(pRes.data?.data || []);
      setStudents(sRes.data?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Enrollment CRUD ────────────────────────────────────────────────────────
  const openAdd = () => { setEnrollForm(emptyEnrollForm); setEnrollModal(true); };
  const openEdit = (row) => {
    setEnrollForm({
      id: row.id,
      studentId: row.studentId ?? "",
      degreeId: row.degreeId ?? "",
      programId: row.programId ?? "",
      startDate: row.startDate ?? "",
      endDate: row.endDate ?? "",
      status: row.status ?? "ACTIVE",
    });
    setEnrollModal(true);
  };
  const closeEnroll = () => { setEnrollModal(false); setEnrollForm(emptyEnrollForm); };

  const handleSaveEnrollment = async () => {
    if (!enrollForm.studentId || !enrollForm.degreeId || !enrollForm.programId
        || !enrollForm.startDate || !enrollForm.status) {
      toast.error("Student, Degree, Program, Start Date and Status are required.");
      return;
    }
    if (enrollForm.status === "COMPLETED" && !enrollForm.endDate) {
      toast.error("End Date is required when status is Completed.");
      return;
    }
    try {
      const payload = {
        ...enrollForm,
        studentId: Number(enrollForm.studentId),
        degreeId: Number(enrollForm.degreeId),
        programId: Number(enrollForm.programId),
      };
      if (enrollForm.id) {
        await axiosInstance.put("/updateEnrollment", payload);
        toast.success("Enrollment updated");
      } else {
        await axiosInstance.post("/saveEnrollment", payload);
        toast.success("Enrollment saved");
      }
      await fetchAll();
      closeEnroll();
    } catch {
      toast.error("Failed to save enrollment");
    }
  };

  // ── Degree CRUD ────────────────────────────────────────────────────────────
  const handleSaveDegree = async () => {
    if (!degreeName.trim()) { toast.error("Degree name is required"); return; }
    try {
      await axiosInstance.post("/saveDegree", { degreeName: degreeName.trim() });
      toast.success("Degree saved");
      setDegreeName("");
      setDegreeModal(false);
      await fetchAll();
    } catch { toast.error("Failed to save degree"); }
  };

  // ── Program CRUD ───────────────────────────────────────────────────────────
  const handleSaveProgram = async () => {
    if (!programForm.programName.trim() || !programForm.degreeId) {
      toast.error("Program name and degree are required");
      return;
    }
    try {
      const payload = {
        id: programForm.id || null,
        programName: programForm.programName.trim(),
        degreeId: Number(programForm.degreeId),
      };
      await axiosInstance.post("/saveProgram", payload);
      toast.success("Program saved");
      setProgramForm({ id: null, programName: "", degreeId: "" });
      setProgramModal(false);
      await fetchAll();
    } catch { toast.error("Failed to save program"); }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    { field: "enrollmentNumber", headerName: "Enrollment No.", width: 140 },
    { field: "studentName",      headerName: "Student Name",   width: 170 },
    { field: "degreeName",       headerName: "Degree",         width: 140 },
    { field: "programName",      headerName: "Program",        width: 190 },
    { field: "startDate",        headerName: "Start Date",     width: 120 },
    {
      field: "endDate", headerName: "End Date", width: 120,
      renderCell: (p) => p.value || <span style={{ color: "#aaa" }}>—</span>,
    },
    {
      field: "status", headerName: "Status", width: 130,
      renderCell: (p) => {
        const c = STATUS_COLORS[p.value] || "#555";
        return (
          <span style={{
            padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
            background: c + "22", color: c,
          }}>
            {p.value ? p.value.charAt(0) + p.value.slice(1).toLowerCase() : ""}
          </span>
        );
      },
    },
    {
      field: "action", headerName: "Action", width: 90,
      renderCell: (p) => (
        <div className="cellAction">
          <div className="editButton" style={{ cursor: "pointer" }} onClick={() => openEdit(p.row)}>
            Edit
          </div>
        </div>
      ),
    },
  ];

  if (loading) return <p>Loading...</p>;

  return (
    <div className="datatable">
      <div className="datatableTitle" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 18, color: "gray", flexGrow: 1 }}>Student Enrollments</span>
        <button onClick={openAdd}                  style={btn("#1976d2")}>+ Add Enrollment</button>
        <button onClick={() => setDegreeModal(true)}  style={btn("#6a1b9a")}>+ Add Degree</button>
        <button onClick={() => setProgramModal(true)} style={btn("#00695c")}>+ Add Program</button>
      </div>

      <DataGrid
        rows={enrollments}
        columns={columns}
        getRowId={(r) => r.id}
        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
        pageSizeOptions={[5, 10, 20]}
        autoHeight
      />

      {/* ── Add / Edit Enrollment ─────────────────────────────────────── */}
      <Modal open={enrollModal} onClose={closeEnroll}>
        <h3 style={{ marginTop: 0 }}>{enrollForm.id ? "Edit Enrollment" : "Add Enrollment"}</h3>

        <Field label="Student *">
          <select value={enrollForm.studentId}
            onChange={(e) => setEnrollForm(f => ({ ...f, studentId: e.target.value }))} style={iStyle}>
            <option value="">-- Select Student --</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.enrollmentNumber})</option>
            ))}
          </select>
        </Field>

        <Field label="Degree *">
          <select value={enrollForm.degreeId}
            onChange={(e) => setEnrollForm(f => ({ ...f, degreeId: e.target.value, programId: "" }))} style={iStyle}>
            <option value="">-- Select Degree --</option>
            {degrees.map(d => <option key={d.id} value={d.id}>{d.degreeName}</option>)}
          </select>
        </Field>

        <Field label="Program *">
          <select value={enrollForm.programId}
            onChange={(e) => setEnrollForm(f => ({ ...f, programId: e.target.value }))}
            style={iStyle} disabled={!enrollForm.degreeId}>
            <option value="">-- Select Program --</option>
            {filteredPrograms.map(p => <option key={p.id} value={p.id}>{p.programName}</option>)}
          </select>
          {!enrollForm.degreeId && (
            <span style={{ fontSize: 11, color: "#888" }}>Select a degree first</span>
          )}
        </Field>

        <Field label="Start Date *">
          <input type="date" value={enrollForm.startDate}
            onChange={(e) => setEnrollForm(f => ({ ...f, startDate: e.target.value }))} style={iStyle} />
        </Field>

        <Field label={`End Date${enrollForm.status === "COMPLETED" ? " *" : " (fill when Completed)"}`}>
          <input type="date" value={enrollForm.endDate}
            onChange={(e) => setEnrollForm(f => ({ ...f, endDate: e.target.value }))} style={iStyle} />
        </Field>

        <Field label="Status *">
          <select value={enrollForm.status}
            onChange={(e) => setEnrollForm(f => ({ ...f, status: e.target.value }))} style={iStyle}>
            {STATUSES.map(s => (
              <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
            ))}
          </select>
        </Field>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={handleSaveEnrollment} style={btn("#1976d2")}>Save</button>
          <button onClick={closeEnroll}          style={btn("#888")}>Cancel</button>
        </div>
      </Modal>

      {/* ── Add Degree ────────────────────────────────────────────────── */}
      <Modal open={degreeModal} onClose={() => setDegreeModal(false)}>
        <h3 style={{ marginTop: 0 }}>Add Degree</h3>
        <Field label="Degree Name *">
          <input value={degreeName} onChange={(e) => setDegreeName(e.target.value)}
            placeholder="e.g. M.Tech, Ph.D" style={iStyle} />
        </Field>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={handleSaveDegree}            style={btn("#6a1b9a")}>Save</button>
          <button onClick={() => setDegreeModal(false)} style={btn("#888")}>Cancel</button>
        </div>
      </Modal>

      {/* ── Add Program ───────────────────────────────────────────────── */}
      <Modal open={programModal} onClose={() => setProgramModal(false)}>
        <h3 style={{ marginTop: 0 }}>Add Program</h3>
        <Field label="Degree *">
          <select value={programForm.degreeId}
            onChange={(e) => setProgramForm(f => ({ ...f, degreeId: e.target.value }))} style={iStyle}>
            <option value="">-- Select Degree --</option>
            {degrees.map(d => <option key={d.id} value={d.id}>{d.degreeName}</option>)}
          </select>
        </Field>
        <Field label="Program Name *">
          <input value={programForm.programName}
            onChange={(e) => setProgramForm(f => ({ ...f, programName: e.target.value }))}
            placeholder="e.g. Artificial Intelligence" style={iStyle} />
        </Field>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={handleSaveProgram}            style={btn("#00695c")}>Save</button>
          <button onClick={() => setProgramModal(false)} style={btn("#888")}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
};

export default StudentEnrollmentList;
